import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Alert, TertiaryButton, Text } from '@enechain/ecloud-designsystem'

import { AlertActions } from '~/components/common/AlertActions'
import { AlertDismissButton } from '~/components/common/AlertDismissButton'
import { Modal, type ModalSize } from '~/components/common/Modal'

type Props = {
  /** 見出し (成功した件数)。 成功した札が無い時は undefined にして本文だけを出す。 */
  title: string | undefined
  /** 本文の文 (理由ごとの件数)。 */
  lines: readonly string[]
  /** 「詳細を見る」 で開くダイアログの見出し。 */
  detailsTitle: string
  detailsSize?: ModalSize
  /** 「詳細を見る」 のダイアログに並べる、 札ごとの理由。 */
  children: React.ReactNode
  onDismiss: () => void
}

/**
 * まとめて行った操作の一部の札だけが通らなかった時のエラーバナー (一括板取消 AC-BIN-112・
 * 入札を全削除 AC-BIN-138。 `ui-message-guide.md`「まとめて行った操作の一部だけが失敗した」)。
 * × で閉じる。 札はバナーに並べず、 「詳細を見る」 で開くダイアログに理由とともに並べる
 * (`conventions.md`「警告・案内で対象を列挙する時の件数と上限」)。
 *
 * 形は design `intraday.html` の `#intraday-clear-partial-alert` / `#intraday-bulk-cancel-partial-alert`
 * (DS `Alert` の error、 導線は UI 辞書 alert-placement §4 の Tertiary sm を右端)。
 */
export const IntradayBulkResultBanner = ({
  title,
  lines,
  detailsTitle,
  detailsSize,
  children,
  onDismiss,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [detailsOpen, setDetailsOpen] = useState(false)
  return (
    <>
      <Alert
        flexShrink={0}
        description={
          <>
            {lines.map((line) => (
              <Text key={line}>{line}</Text>
            ))}
          </>
        }
        status="error"
        title={title}
      >
        <AlertActions>
          <TertiaryButton onClick={() => setDetailsOpen(true)} size="sm">
            {t('page:biddingIntraday.bulkResult.showDetails')}
          </TertiaryButton>
          <AlertDismissButton onClick={onDismiss} />
        </AlertActions>
      </Alert>
      <Modal
        footer={
          <TertiaryButton onClick={() => setDetailsOpen(false)} size="md">
            {t('common:action.close')}
          </TertiaryButton>
        }
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        size={detailsSize}
        title={detailsTitle}
      >
        {children}
      </Modal>
    </>
  )
}
